/* CT-2: the counterparty card's Contacts tab — the people at the counterparty and how to reach them.

   One list (`excalibur/counterparty-contacts/?counterparty=`, primary first, then by last and first
   name), one page of 100 — a counterparty has a handful — re-read after every write. Writes (add,
   edit, delete, make primary) for every role that can sign in — all of them may write the card.

   Channels stack in their cell, each with its type's glyph and NAME (`contactChannels.js`); phone
   and Viber dial, e-mail mails, a website opens in a new browser tab. Past three, the cell shows two
   and "+N", which expands IN PLACE (and collapses back): the channels are links, and a link inside
   a tooltip cannot be reached by keyboard or by touch. A deactivated contact's channels are plain
   text: it is not offered for contact, so nothing in its row dials or mails.

   Status is pills only, all `statusTag` (`status.js`): the colour says ACTIVITY, the star says
   primary — "Primary" (`ok` + star; a primary contact is always active), "Active" (`ok`) and
   "Deactivated" (the module's neutral `off`, `cp-status--off`). "Make primary" lives in the row
   menu, for an active non-primary contact only — one click, `PATCH {is_primary: true}` (the API
   takes the flag off the previous primary). A deactivated row is greyed by TEXT colour
   (`.cp-row--inactive`), never by opacity.

   Deactivate is the API's DELETE: it sets `is_removed` and the contact stays on the list. The
   primary cannot be deactivated (the API answers 400), so the item is disabled with the reason;
   a deactivated contact is not offered it again — "Active" in the modal brings it back. */

import React, { useEffect, useState } from "react";
import { Button, Empty, Flex, Modal, Skeleton, Table, Tag, Tooltip, Typography } from "antd";
import { EditOutlined, PlusOutlined, StarFilled, StarOutlined, StopOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import { RowActions, actionsColumn, dash } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { call } from "../../util/action";
import { translate } from "../../util/util";

import ContactModal, { contactName } from "./ContactModal";
import { CHANNEL_ICON, channelHref, channelLabelKey } from "./contactChannels";
import { statusTag } from "./status";
import { LoadError } from "./tabParts";

const URL = "excalibur/counterparty-contacts/";
const COLLAPSE_OVER = 3;
const COLLAPSED_SHOWN = 2;
const toast = { position: "top-right" };
const STATUS_TONE = { primary: "ok", active: "ok", deactivated: "off" };
const STATUS_LABEL = { primary: "ct_primary", active: "ct_active", deactivated: "ct_deactivated" };

function Channel({ channel, off, t }) {
  const Icon = CHANNEL_ICON[channel.channel_type] || CHANNEL_ICON.other;
  const type = t(channelLabelKey(channel.channel_type));
  const href = off ? null : channelHref(channel);
  const external = channel.channel_type === "website";
  return (
    <Flex align="center" gap={6} className="cp-ctcell__row">
      <Tooltip title={type}>
        <Icon className="cp-ctcell__icon" aria-label={type} role="img" />
      </Tooltip>
      {href ? (
        <a
          className="cp-doclink cp-doclink--plain"
          href={href}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          title={channel.comment || channel.value}>
          {channel.value}
        </a>
      ) : (
        <Typography.Text title={channel.comment || channel.value} ellipsis>
          {channel.value}
        </Typography.Text>
      )}
    </Flex>
  );
}

function ChannelsCell({ channels, off, open, onToggle, t }) {
  if (!channels.length) return dash;
  const folded = channels.length > COLLAPSE_OVER && !open;
  const shown = folded ? channels.slice(0, COLLAPSED_SHOWN) : channels;
  return (
    <Flex vertical gap={2} className="cp-ctcell">
      {shown.map(ch => (
        <Channel key={ch.id} channel={ch} off={off} t={t} />
      ))}
      {channels.length > COLLAPSE_OVER ? (
        <Button type="link" size="small" className="cp-ctcell__more" aria-expanded={!folded} onClick={onToggle}>
          {folded ? t("ct_more").replace("{n}", channels.length - COLLAPSED_SHOWN) : t("ct_collapse")}
        </Button>
      ) : null}
    </Flex>
  );
}

export default function ContactsTab({ counterpartyId, lang, onCountsChange }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [open, setOpen] = useState({});
  /* `{row}` while the modal is open: `{row: null}` for a new contact. */
  const [modal, setModal] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    setError(false);
    /* Two quick writes mean two reloads; only the newer answer may land. */
    let stale = false;
    call({ method: "GET", url: `${URL}?counterparty=${counterpartyId}&page_size=100` }, response => {
      if (stale) return;
      if (!response || response.failed || !Array.isArray(response.results)) {
        setRows([]);
        setError(true);
        return;
      }
      setRows(response.results);
    });
    return () => {
      stale = true;
    };
  }, [counterpartyId, reload]);

  const again = () => setReload(k => k + 1);
  const primary = (rows || []).find(r => r.is_primary) || null;

  const makePrimary = row => {
    setBusyId(row.id);
    call({ method: "PATCH", url: `${URL}${row.id}/`, post: { is_primary: true } }, response => {
      setBusyId(null);
      if (response?.id) Alert.success(t("ct_primary_msg"), toast);
      /* A 5xx / network failure was already toasted by `action.js`. */ else if (!response?.failed) Alert.warning(t("edit_error_msg"), toast);
      again();
    });
  };

  /* Destructive, so confirmed — naming the contact, the verb on the button (spec/03). */
  const deactivate = row =>
    Modal.confirm({
      title: t("ct_deactivate_title").replace("{name}", contactName(row)),
      content: t("ct_active_hint"),
      okText: t("ct_deactivate"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () =>
        new Promise(resolve =>
          call({ method: "DELETE", url: `${URL}${row.id}/` }, response => {
            resolve();
            if (response?.status === 204) Alert.success(t("ct_deactivated_msg"), toast);
            else if (response?.detail) Alert.warning(t("edit_error_msg"), toast);
            again();
          })
        ),
    });

  const columns = [
    {
      key: "contact",
      title: t("ct_col_contact"),
      render: (v, row) => (
        <Flex vertical className="lp-main">
          <Typography.Text strong>{contactName(row) || dash}</Typography.Text>
          {row.position ? (
            <Typography.Text type="secondary" className="lp-main__sub" ellipsis={{ tooltip: row.position }}>
              {row.position}
            </Typography.Text>
          ) : null}
        </Flex>
      ),
    },
    { key: "role", title: t("ct_col_role"), render: (v, row) => (row.role?.label ? <Typography.Text>{row.role.label}</Typography.Text> : dash) },
    {
      key: "channels",
      title: t("ct_col_channels"),
      render: (v, row) => (
        <ChannelsCell
          channels={row.channels || []}
          off={row.is_removed}
          open={!!open[row.id]}
          onToggle={() => setOpen(prev => ({ ...prev, [row.id]: !prev[row.id] }))}
          t={t}
        />
      ),
    },
    {
      key: "status",
      title: t("ct_col_status"),
      render: (v, row) => {
        const key = row.is_primary ? "primary" : row.is_removed ? "deactivated" : "active";
        return statusTag(STATUS_TONE, key, lang, { label: t(STATUS_LABEL[key]), icon: row.is_primary ? <StarFilled /> : undefined });
      },
    },
    actionsColumn((v, row) => (
      <RowActions
        label={`${t("row_actions")}: ${contactName(row)}`}
        items={[
          { key: "edit", label: t("edit_btn"), icon: <EditOutlined /> },
          row.is_primary || row.is_removed ? null : { key: "primary", label: t("ct_make_primary"), icon: <StarOutlined />, disabled: busyId === row.id },
          row.is_removed
            ? null
            : {
                key: "deactivate",
                label: row.is_primary ? <Tooltip title={t("ct_primary_not_deactivated")}>{t("ct_deactivate")}</Tooltip> : t("ct_deactivate"),
                icon: <StopOutlined />,
                danger: true,
                disabled: row.is_primary,
              },
        ].filter(Boolean)}
        onClick={key => (key === "edit" ? setModal({ row }) : key === "primary" ? makePrimary(row) : deactivate(row))}
      />
    )),
  ];

  const addButton = (
    <Button icon={<PlusOutlined />} onClick={() => setModal({ row: null })}>
      {t("ct_add")}
    </Button>
  );

  return (
    <section className="cp-block cp-cttab" aria-label={t("tab_contacts")}>
      <Flex className="cp-block__head" align="center" justify="space-between" gap="small" wrap>
        <Flex align="center" gap="small" wrap>
          <Tag className="cp-flag">{t("picsell_data_tag")}</Tag>
          {rows && !error ? (
            <Typography.Text type="secondary" className="lp-num">
              {t("ct_records").replace("{n}", rows.length)}
            </Typography.Text>
          ) : null}
        </Flex>
        {addButton}
      </Flex>

      {rows === null ? (
        <Skeleton active title={false} paragraph={{ rows: 3 }} />
      ) : (
        <Table
          className="lp-table__grid lp-fit"
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rows}
          pagination={false}
          rowClassName={row => (row.is_removed ? "cp-row--inactive" : "")}
          scroll={{ x: "max-content" }}
          locale={{
            emptyText: error ? (
              <LoadError lang={lang} onRetry={again} />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("ct_empty")}>
                {addButton}
              </Empty>
            ),
          }}
        />
      )}

      {modal ? (
        <ContactModal
          key={modal.row?.id || "new"}
          row={modal.row}
          counterpartyId={counterpartyId}
          lang={lang}
          otherPrimary={primary && primary.id !== modal.row?.id ? primary : null}
          /* The counterparty's other ACTIVE contacts, as this tab loaded them — the modal checks a
             new or changed channel against theirs without a request of its own. */
          others={(rows || []).filter(r => r.id !== modal.row?.id && !r.is_removed)}
          onClose={() => setModal(null)}
          /* A new contact changes the counterparty's count; an edit (or a deactivation, which keeps
             the contact on the list) does not. */
          onSaved={() => {
            again();
            if (!modal.row) onCountsChange?.();
          }}
        />
      ) : null}
    </section>
  );
}
